import { styled } from '@ds/coolshop';
import { useLanguageCtx } from '@src/_new/modules/language';
import { useCompanyOverrides } from '@src/_new/overrides/hooks/useCompanyOverrides';
import { path as translationsPath } from '@src/components/translations/Translations';
import { useAuth } from '@src/hooks';
import Layout from '@src/Layout';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { lazy, Suspense, useContext, useEffect, useState } from 'react';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
} from 'react-router-dom';

import { LinearLoader } from '../shared/LinearLoader';

const LanguageCreatePage = lazy(() => import('@pages/LanguageCreatePage'));
const LanguageEditPage = lazy(() => import('@pages/LanguageEditPage'));
const LanguageListPage = lazy(() => import('@pages/LanguageListPage'));
const SearchListPage = lazy(() => import('@pages/SearchListPage'));
const SearchDetailPage = lazy(() => import('@pages/SearchDetailPage'));
const ShortListPage = lazy(() => import('@pages/ShortListPage'));
const PrototypesPage = lazy(() => import('@pages/PrototypesPage'));
const ProductsPage = lazy(() => import('@pages/ProductsPage'));
const JobListPage = lazy(() => import('@pages/JobListPage'));

const ThreeDViewerPage = lazy(() => import('@pages/ThreeDViewerPage'));
// todo
const Hierarchies = lazy(() => import('@src/modules/hierarchy/HierarchyViewer'));
const Discounts = lazy(() => import('@src/modules/discounts/Discounts'));
const Translations = lazy(() => import('@src/components/translations/Translations'));
const SettingsPage = lazy(() => import('@pages/SettingsPage'));
const JsonataDependencies = lazy(
    () => import('@src/_new/modules/jsonata-dependencies/JsonataDependencies'),
);
const DataQualityPage = lazy(() => import('@pages/DataQualityPage'));
const OffersPage = lazy(() => import('@pages/OffersPage'));
const OfferDetailPage = lazy(() => import('@pages/OfferDetailPage'));
const CustomPageListPage = lazy(() => import('@pages/CustomPageListPage'));
const CustomPageDetailPage = lazy(() => import('@pages/CustomPageDetailPage'));
const AdminDashboardPage = lazy(() => import('@pages/AdminDashboardPage'));
const CompanyAdminPage = lazy(() => import('@pages/CompanyAdminPage'));
const ConfigitPage = lazy(() => import('@pages/ConfigitPage'));

const classes = {
    loading: `loading`,
};

const Root = styled('div')(({ theme }) => ({
    [`&.${classes.loading}`]: {
        padding: theme.spacing(1),
    },
}));

function DelayedFallback(): JSX.Element | null {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 150);
        return () => clearTimeout(timeout);
    }, []);

    if (!show) {
        return null;
    }
    return (
        <Root className={classes.loading}>
            <LinearLoader />
        </Root>
    );
}

/**
 * App routes
 * "/language"                          >> LANGUAGE FRAME
 * "/language/new"                      >> NEW LANGUAGE FRAME
 * "/language/:id"                      >> EDIT LANGUAGE id
 * "/products                           >> PRODUCTS WORKFLOW FRAME
 * "/hierarchies/*"                     >> HIERARCHIES FRAME
 * "/translations"                      >> TRANSLATIONS FRAME
 * "/hierarchies/:hierarchyId/:nodeId"  >> EDIT TRANSLATIONS
 */
export function Routes(): JSX.Element {
    const { hasUserRole } = useAuth();
    const { languages } = useLanguageCtx();
    const { enableContentTranslation, jsonataDependenciesEnabled, defaultPageUri } =
        useContext(SettingsContext);
    const { routes: companyRoutes, defaultRoute: companyDefaultRoute } = useCompanyOverrides();

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route element={<Layout />}>
                {hasUserRole('PIM_ADMIN') && (
                    <>
                        <Route path="language/new" element={<LanguageCreatePage />} />
                        <Route path="language/:id" element={<LanguageEditPage />} />
                        <Route path="language" element={<LanguageListPage />} />

                        <Route
                            path="products/"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                        <Route
                            path="products/:id"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                        <Route
                            path="products/edit/:id"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                        <Route
                            path="products/:id/:code"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                        <Route
                            path="products/:id/edit/:code"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                        <Route
                            path="products/:id/:code/:typecode"
                            index
                            element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                        />
                    </>
                )}
                {hasUserRole('PIM_DEMO_3D') && (
                    <Route
                        path="3d-viewer"
                        element={languages ? <ThreeDViewerPage /> : <Navigate to="/language" />}
                    />
                )}
                {hasUserRole('CGT_PRICELIST') && (
                    <Route
                        path="short_lists"
                        element={
                            languages ? (
                                <ShortListPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                )}
                {(hasUserRole('CGT_PROTOTYPES') || hasUserRole('PIM_PROTOTYPE')) && (
                    <Route
                        path="prototypes/:cgtModelId"
                        element={
                            languages ? (
                                <PrototypesPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                )}
                <Route
                    path="hierarchies/*"
                    index
                    element={
                        languages ? (
                            <Hierarchies />
                        ) : (
                            <Navigate to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'} />
                        )
                    }
                />
                <Route
                    path="search"
                    element={
                        languages ? (
                            <SearchListPage />
                        ) : (
                            <Navigate to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'} />
                        )
                    }
                />
                <Route
                    path="search/results/:objectSchema"
                    element={
                        languages ? (
                            <SearchDetailPage />
                        ) : (
                            <Navigate to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'} />
                        )
                    }
                />
                <Route
                    path="discounts"
                    element={
                        languages ? (
                            <Discounts />
                        ) : (
                            <Navigate to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'} />
                        )
                    }
                />

                <Route
                    path="jobs"
                    element={languages ? <JobListPage /> : <Navigate to="/language" />}
                />
                {hasUserRole('PIM_SUPERADMIN') && (
                    <Route
                        path="settings"
                        element={
                            languages ? (
                                <SettingsPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_SUPERADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                )}

                {hasUserRole('PIM_SUPERADMIN') && (
                    <Route path="admin-dashboard" element={<AdminDashboardPage />} />
                )}

                {hasUserRole('COMPANY_ADMIN') && (
                    <Route path="company-admin" element={<CompanyAdminPage />} />
                )}

                {hasUserRole('PIM_CONFIGIT_ADMIN') && (
                    <Route path="configit" element={<ConfigitPage />} />
                )}

                {(hasUserRole('PIM_PRICING') || hasUserRole('PIM_ADMIN')) && (
                    <Route
                        path="data-quality"
                        element={
                            languages ? (
                                <DataQualityPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/hierarchies'}
                                />
                            )
                        }
                    />
                )}

                {hasUserRole('PIM_CPQ') && (
                    <>
                        <Route
                            path="offers"
                            element={
                                languages ? (
                                    <OffersPage />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/hierarchies'}
                                    />
                                )
                            }
                        />
                        <Route
                            path="offers/:offerId"
                            element={
                                languages ? (
                                    <OfferDetailPage />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/hierarchies'}
                                    />
                                )
                            }
                        />
                    </>
                )}

                {hasUserRole('PIM_ADMIN') && jsonataDependenciesEnabled === true && (
                    <Route
                        path="jsonata-dependencies"
                        element={languages ? <JsonataDependencies /> : <Navigate to="/language" />}
                    />
                )}

                <Route
                    path="pages/:slug"
                    element={
                        languages ? (
                            <CustomPageListPage />
                        ) : (
                            <Navigate
                                to={hasUserRole('PIM_ADMIN') ? '/language' : '/hierarchies'}
                            />
                        )
                    }
                />
                <Route
                    path="pages/:slug/:nodeId"
                    element={
                        languages ? (
                            <CustomPageDetailPage />
                        ) : (
                            <Navigate
                                to={hasUserRole('PIM_ADMIN') ? '/language' : '/hierarchies'}
                            />
                        )
                    }
                />

                {companyRoutes.map(route => (
                    <Route key={route.path} path={route.path} element={<route.component />} />
                ))}

                <Route
                    path="*"
                    element={
                        <Navigate
                            to={getDefaultRoute(
                                hasUserRole('PIM_ADMIN'),
                                companyDefaultRoute,
                                defaultPageUri,
                                hasUserRole,
                            )}
                        />
                    }
                />

                {enableContentTranslation && (
                    <>
                        <Route path={translationsPath} element={<Translations />} />
                        <Route
                            path={`${translationsPath}/:hierarchyId/:nodeId`}
                            element={<Translations />}
                        />
                    </>
                )}
            </Route>,
        ),
    );

    // let span = webTracerWithZone.startSpan(`Router`, contenxt.active());
    // router.subscribe(routeState => {
    //     if (span) {
    //         span.end();
    //     }
    //     span = webTracerWithZone.startSpan(`Router`);
    // });

    return (
        <Suspense fallback={<DelayedFallback />}>
            <RouterProvider router={router} />
        </Suspense>
    );
}

function getDefaultRoute(
    isAdmin: boolean,
    companyDefaultRoute?: string | null,
    defaultPageUri?: {
        uri: string;
        roleOverrides?: Array<{ role: string; uri: string }> | null;
    } | null,
    hasUserRole?: (role: string) => boolean,
) {
    if (defaultPageUri?.roleOverrides && hasUserRole) {
        const match = defaultPageUri.roleOverrides.find(r => hasUserRole(r.role));
        if (match) return match.uri;
    }
    if (defaultPageUri?.uri) {
        return defaultPageUri.uri;
    }
    if (companyDefaultRoute) {
        return companyDefaultRoute;
    }
    return isAdmin ? '/language' : '/hierarchies';
}

export default Routes;
